// apps/web/src/features/admin/form-config/layouts/LayoutsPanel.tsx
import { useState } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import { apiErrorCode } from "@/lib/api-client";
import { useLayoutVariants, type LayoutVariantDto } from "./api";
import { timeAgo } from "@/features/queues/lib/timeAgo";
import "./LayoutsPanel.css";

interface LayoutsPanelProps {
  jobTypeId: string;
  formId: string | null;
}

/** A variant this divergent has kept arriving in a shape none of a form's
 *  declared layouts fit — the signal that a new layout exists but nobody
 *  has declared it yet (Task 10 spec threshold). */
const DIVERGENT_NOTICE_THRESHOLD = 5;

/** Maps the layout-variant route's 409/400 error codes to operator-friendly
 *  text. The baseline guards (cannot_retire_baseline, cannot_delete_baseline,
 *  cannot_merge_baseline) are belt-and-suspenders here — the UI never offers
 *  those actions on a baseline row — but a stale row set from another tab
 *  could still race one in, so a real message beats a raw 409. */
function errorMessage(code: string): string {
  if (code === "cannot_retire_baseline") return "The baseline layout can't be retired.";
  if (code === "cannot_delete_baseline") return "The baseline layout can't be deleted.";
  if (code === "cannot_merge_baseline") return "The baseline layout can't be merged away.";
  if (code === "cannot_merge_into_self") return "Choose a different layout to merge into.";
  if (code === "target_not_in_form") return "That layout no longer belongs to this form.";
  return "Could not update layout.";
}

function lifecycleLabel(v: LayoutVariantDto): string {
  if (v.isBaseline) return "Baseline";
  return v.status === "retired" ? "Retired" : "Active";
}

interface RowProps {
  variant: LayoutVariantDto;
  rowError: string | null;
  onRename: (label: string) => void;
  onRetire: () => void;
  onRestore: () => void;
  onRequestMerge: () => void;
  onRequestDelete: () => void;
}

function LayoutRow({
  variant,
  rowError,
  onRename,
  onRetire,
  onRestore,
  onRequestMerge,
  onRequestDelete,
}: RowProps) {
  const [label, setLabel] = useState(variant.label);
  const showDivergentNotice = variant.divergentObservationCount >= DIVERGENT_NOTICE_THRESHOLD;

  return (
    <tr className="layouts-panel__row" data-testid={`layout-row-${variant.id}`}>
      <td>
        <div className="layouts-panel__label-edit">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            aria-label={`Label for ${variant.label}`}
          />
          <button
            type="button"
            onClick={() => onRename(label)}
            disabled={label.trim() === "" || label === variant.label}
          >
            Save
          </button>
        </div>
        {showDivergentNotice && (
          <p className="layouts-panel__divergent-notice" role="status">
            This form keeps arriving in a shape none of its layouts fit.
          </p>
        )}
        {variant.isStale && (
          <p className="layouts-panel__stale-suggestion" role="status">
            Suggestion: this layout hasn&apos;t matched recently — consider retiring it.
          </p>
        )}
        {rowError && (
          <p className="layouts-panel__inline-error" role="alert">
            {rowError}
          </p>
        )}
      </td>
      <td className="layouts-panel__lifecycle">
        <span
          className="layouts-panel__badge"
          data-status={variant.isBaseline ? "baseline" : variant.status}
        >
          {lifecycleLabel(variant)}
        </span>
      </td>
      <td>{variant.observationCount}</td>
      <td>{variant.divergentObservationCount}</td>
      <td>{variant.lastMatchedAt ? timeAgo(variant.lastMatchedAt) : "Never"}</td>
      <td className="layouts-panel__actions">
        {!variant.isBaseline && (
          <>
            {variant.status === "retired" ? (
              <button type="button" onClick={onRestore}>
                Restore
              </button>
            ) : (
              <button type="button" onClick={onRetire}>
                Retire
              </button>
            )}
            <button type="button" onClick={onRequestMerge}>
              Merge…
            </button>
            <button type="button" className="layouts-panel__delete-btn" onClick={onRequestDelete}>
              Delete
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

/**
 * LayoutsPanel — Task 12 admin surface for FormLayoutVariant (rename, retire/
 * restore, merge, delete). Only ever mounted when the job type has
 * `allowLayoutVariants` true — the FormConfigPage tab strip hides the
 * `layouts` tab entirely otherwise, so there is no empty-tab state to design
 * for here.
 *
 * Follows the `../forms/ManageFormsDialog.tsx` shape: a `use*` data hook
 * mirroring `useJobTypeForms`, per-row inline rename, and inline 409 error
 * surfacing keyed by row id.
 */
export function LayoutsPanel({ jobTypeId, formId }: LayoutsPanelProps) {
  const { variants, loading, rename, setStatus, merge, remove } = useLayoutVariants(jobTypeId, formId);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [mergingId, setMergingId] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Every row action (rename, retire, restore, merge, delete) shares the same
  // shape: clear any prior error for the row, run the mutation, and surface a
  // friendly message keyed by row id on failure.
  async function runRowAction(id: string, action: () => Promise<void>) {
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      await action();
    } catch (err) {
      setRowErrors((prev) => ({ ...prev, [id]: errorMessage(apiErrorCode(err)) }));
    }
  }

  function openMerge(id: string) {
    setMergingId(id);
    setMergeTarget(null);
  }

  async function confirmMerge() {
    if (!mergingId || !mergeTarget) return;
    const sourceId = mergingId;
    setMergingId(null);
    await runRowAction(sourceId, () => merge(sourceId, mergeTarget));
    setMergeTarget(null);
  }

  async function confirmDelete() {
    if (!deletingId) return;
    const id = deletingId;
    setDeletingId(null);
    await runRowAction(id, () => remove(id));
  }

  if (loading) return <div className="layouts-panel">Loading…</div>;

  if (variants.length === 0) {
    return (
      <div className="layouts-panel">
        <p className="layouts-panel__empty">No layouts declared for this form yet.</p>
      </div>
    );
  }

  const mergingVariant = variants.find((v) => v.id === mergingId) ?? null;
  const deletingVariant = variants.find((v) => v.id === deletingId) ?? null;

  return (
    <div className="layouts-panel">
      <table className="layouts-panel__table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Lifecycle</th>
            <th>Observations</th>
            <th>Divergent</th>
            <th>Last matched</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => (
            <LayoutRow
              key={v.id}
              variant={v}
              rowError={rowErrors[v.id] ?? null}
              onRename={(label) => void runRowAction(v.id, () => rename(v.id, label))}
              onRetire={() => void runRowAction(v.id, () => setStatus(v.id, "retired"))}
              onRestore={() => void runRowAction(v.id, () => setStatus(v.id, "active"))}
              onRequestMerge={() => openMerge(v.id)}
              onRequestDelete={() => setDeletingId(v.id)}
            />
          ))}
        </tbody>
      </table>

      {mergingVariant && (
        <div role="dialog" aria-modal="true" aria-label="Merge layout" className="layouts-panel__merge-dialog">
          <div className="layouts-panel__merge-panel">
            <h2>Merge &ldquo;{mergingVariant.label}&rdquo;</h2>
            <p>
              Choose the layout to merge &ldquo;{mergingVariant.label}&rdquo; into. The two layouts
              are combined into one — their learned fingerprint and field positions are summed
              into the layout you choose below, and this row is removed. This cannot be undone.
            </p>
            <div className="layouts-panel__merge-targets" role="radiogroup" aria-label="Merge target">
              {variants
                .filter((v) => v.id !== mergingVariant.id)
                .map((v) => (
                  <label key={v.id} className="layouts-panel__merge-target-option">
                    <input
                      type="radio"
                      name="merge-target"
                      value={v.id}
                      checked={mergeTarget === v.id}
                      onChange={() => setMergeTarget(v.id)}
                    />
                    {v.label}
                  </label>
                ))}
            </div>
            <div className="layouts-panel__merge-actions">
              <button type="button" onClick={() => setMergingId(null)}>
                Cancel
              </button>
              <button type="button" disabled={!mergeTarget} onClick={() => void confirmMerge()}>
                Merge
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deletingVariant !== null}
        title={deletingVariant ? `Delete "${deletingVariant.label}"?` : ""}
        description="This permanently removes the layout and its learned fingerprint. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeletingId(null)}
      />
    </div>
  );
}
